import { useState } from "react";
import { GripVertical, Link, Sparkles} from "lucide-react";
import { ListItem, ListItemType } from "modules/lists/types/lists";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";

import {
    Item,
    ItemActions,
    ItemContent,
    ItemDescription,
    ItemMedia,
} from "@/components/ui/item"
import { Checkbox } from "@/components/ui/checkbox";
import { AddDeleteList } from "./addDeleteLIst";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card";

interface ListItemRowProps {
    item: ListItem;
    isEditMode?: boolean;
    /** When true the row is rendered with strikethrough and moves to the excluded section. */
    isChecked?: boolean;
    /** Per-item error message from the autosave hook. */
    saveError?: string;
    onEdit: (item: ListItem) => void;
    onDelete: (itemId: string) => void;
    /**
     * Called when the user changes the quantity via the inline +/− controls.
     * In edit mode the parent persists to DB; in view mode it only updates local state.
     */
    onQuantityChange?: (item: ListItem, newQty: number) => void;
    /** Called when the checkbox is toggled in view mode. */
    onToggleCheck?: () => void;
    /** Called when FREE item text is committed via inline input. Only wired in edit mode. */
    onRawDescriptionChange?: (itemId: string, newRawDescription: string) => void;
}

/**
 * Renders a single list item row with description, quantity/unit,
 * a type badge (FREE vs LINKED), edit/delete actions, and an optional
 * inline quantity stepper.
 */
const ListItemRow: React.FC<ListItemRowProps> = ({
    item,
    isEditMode = false,
    isChecked = false,
    saveError,
    onEdit,
    onDelete,
    onQuantityChange,
    onToggleCheck,
    onRawDescriptionChange,
}) => {
    const [isEditingText, setIsEditingText] = useState(false);
    const [editText, setEditText] = useState("");

    /**
     * Commits the inline text edit after trimming and comparing with the original.
     * Rejects empty/whitespace-only text and unchanged values.
     */
    const handleTextCommit = () => {
        if (!isEditingText) return;
        const trimmed = editText.trim();
        if (trimmed && trimmed !== item.rawDescription) {
            onRawDescriptionChange?.(item.id, trimmed);
        }
        setIsEditingText(false);
    };

    /**
     * Handles key events on the inline editing input.
     * Enter commits, Escape reverts to the original text.
     */
    const handleTextKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === "Enter") {
            e.preventDefault();
            handleTextCommit();
        } else if (e.key === "Escape") {
            setEditText(item.rawDescription);
            setIsEditingText(false);
        }
    };

    const handleDecrement = () => {
        if (isEditMode) {
            // Edit mode: reaching 1 triggers deletion
            if (item.quantity <= 1) {
                onDelete(item.id);
            } else {
                onQuantityChange?.(item, item.quantity - 1);
            }
        } else {
            // View mode: allow reaching 0 — parent marks the item as excluded
            if (item.quantity > 0) {
                onQuantityChange?.(item, item.quantity - 1);
            }
        }
    };

    const handleIncrement = () => {
        onQuantityChange?.(item, item.quantity + 1);
    };

    return (
        <Item
            variant="muted"
            size="sm"
            className="w-full max-w-full min-w-0 overflow-hidden flex-nowrap"
            asChild
        >
            <div className="min-w-0">
                <ItemMedia className="shrink-0">
                    {isEditMode
                        ? <GripVertical className="h-4 w-4 text-muted-foreground cursor-grab" />
                        : (
                            <div onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}>
                                <Checkbox checked={isChecked} onCheckedChange={() => onToggleCheck?.()} />
                            </div>
                        )
                    }
                </ItemMedia>

                <ItemContent className="flex-1 basis-0 min-w-0 overflow-hidden">
                    <div
                        data-slot="item-title"
                        className={cn(
                            "block w-full max-w-full min-w-0 truncate text-sm leading-snug font-medium",
                            isChecked && "line-through opacity-60"
                        )}
                    >
                        {isEditMode && item.type === ListItemType.FREE ? (
                            isEditingText ? (
                                <input
                                    autoFocus
                                    value={editText}
                                    onChange={(e) => setEditText(e.target.value)}
                                    onBlur={handleTextCommit}
                                    onKeyDown={handleTextKeyDown}
                                    className="w-full bg-transparent text-sm font-medium outline-none p-0 border-none"
                                />
                            ) : (
                                <span
                                    className="block w-full cursor-text truncate"
                                    onClick={() => {
                                        setEditText(item.rawDescription);
                                        setIsEditingText(true);
                                    }}
                                >
                                    {item.rawDescription}
                                </span>
                            )
                        ) : (
                            <span>{item.linkedProductName??item.rawDescription}</span>                          
                        )}
                    </div>

                    <ItemDescription className="min-w-0 max-w-full overflow-hidden">
                        {item.type === ListItemType.LINKED ? (
                            <HoverCard>
                                <HoverCardTrigger asChild>
                                    <button type="button" className="block max-w-full min-w-0 overflow-hidden">
                                        <Badge
                                            variant={"outline"}
                                            className="text-muted-foreground max-w-full min-w-0 overflow-hidden flex items-center gap-1"
                                        >
                                            <Link className="h-3.5 w-3.5 shrink-0 best-price" />
                                            <span className="min-w-0 truncate leading-none">
                                            Marca fija
                                            </span>
                                        </Badge>
                                    </button>
                                </HoverCardTrigger>
                                <HoverCardContent>
                                    {item.linkedProductName}
                                </HoverCardContent>
                            </HoverCard>
                        ) : (
                            <Badge variant={"outline"} className="text-muted-foreground">
                                <Sparkles className="h-3.5 w-3.5 text-primary" /> Optimizable
                            </Badge>
                        )}
                        {saveError && (
                            <span className="text-xs text-destructive mt-0.5 block">{saveError}</span>
                        )}
                    </ItemDescription>
                </ItemContent>

                <ItemActions className="shrink-0 ml-auto">
                    {!isChecked && (
                        <AddDeleteList
                            currQuantity={item.quantity}
                            onIncrement={handleIncrement}
                            onDecrement={handleDecrement}
                        />
                    )}
                </ItemActions>
            </div>
        </Item>
    );
};

export { ListItemRow };
